import { useRef, useState, useEffect } from "react";
import {
  MapBannerSwiperWrapper,
  MapBannerWrapper,
  BannerItem,
} from "../Style/SearchStyles";
import { handleTouchEvents } from "../../NewSearch/Hook/touchEventHandlers";
import { StoreList } from "../List/StoreList";
import { FilterComponent } from "../List/FilterComponent";
import { PremiumStoreList } from "../List/PremiumStoreList";
import { navigatorShadowState } from "../../../../recoil/navigator";
import { useSetRecoilState } from "recoil";
import { useUI } from "../../NewSearch/Hook/UIContext";
import { useStore } from "../../NewSearch/Hook/StoreContext";
import { throttle } from "lodash";
import { Autoplay } from "swiper/modules";
import { SwiperSlide, Swiper } from "swiper/react";
import {
  SearchBannerResponse,
  getSearchBanner,
} from "../../../../api/search-banner";
import type { Swiper as SwiperType } from "swiper";
import { useSearchContext } from "../../NewSearch/Hook/SearchContext";

export const MapBanner = () => {
  const {
    setShowStoreList,
    showStoreList,
    setIsScrolled,
    setIsDragging: setGlobalDragging,
  } = useUI();
  const { setSelectedStore } = useStore();
  const { recommendedList } = useSearchContext();
  const [startY, setStartY] = useState<number | null>(null);
  const [currentY, setCurrentY] = useState<number>(0);
  const [startX, setStartX] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const setNavigatorShadow = useSetRecoilState(navigatorShadowState);
  const [data, setData] = useState<SearchBannerResponse[]>([]);
  const swiperRef = useRef<SwiperType>();

  useEffect(() => {
    const timer = setTimeout(() => {
      swiperRef.current?.autoplay.start();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const getData = async () => {
    try {
      const res = await getSearchBanner();
      setData(res);
    } catch (e) {
      setData([]);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    setGlobalDragging(isDragging);
  }, [isDragging]);

  const { handleTouchStart, handleTouchMove, handleTouchEnd } =
    handleTouchEvents(
      startY,
      setStartY,
      startX,
      setStartX,
      setCurrentY,
      setIsDragging,
      () => {
        setSelectedStore(null);
        if (currentY > 0) {
          setTimeout(() => {
            setNavigatorShadow(true);
            setShowStoreList(true);
          }, 50);
        }
      }
    );

  useEffect(() => {
    if (!showStoreList && bannerRef.current) {
      bannerRef.current.scrollTop = 0;
    }
  }, [showStoreList]);

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (bannerRef.current) {
        const scrollTop = bannerRef.current.scrollTop;
        setIsScrolled(scrollTop > 50);
      }
    }, 200); // 200ms마다 실행

    if (bannerRef.current) {
      bannerRef.current.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (bannerRef.current) {
        bannerRef.current.removeEventListener("scroll", handleScroll);
        handleScroll.cancel(); // 스로틀링된 함수 취소
      }
    };
  }, [setIsScrolled]);

  return (
    <MapBannerWrapper
      ref={bannerRef}
      onTouchStart={!showStoreList ? handleTouchStart : undefined}
      onTouchMove={!showStoreList ? handleTouchMove : undefined}
      onTouchEnd={!showStoreList ? handleTouchEnd : undefined}
      onMouseDown={!showStoreList ? handleTouchStart : undefined}
      onMouseMove={!showStoreList ? handleTouchMove : undefined}
      style={{
        top: showStoreList ? "0" : "calc(100% - 90px)",
        transition: showStoreList ? "all 0.5s ease" : "all 0.7s ease",
        position: "fixed",
      }}
    >
      {!showStoreList && (
        <>
          <div className="line"></div>
          <MapBannerSwiperWrapper>
            <Swiper
              className="banner-swiper"
              onSwiper={(swiper: SwiperType) => (swiperRef.current = swiper)}
              slidesPerView={1.1}
              centeredSlides={true}
              autoplay={{ delay: 4000, disableOnInteraction: false }}
              observer
              observeParents
              modules={[Autoplay]}
              spaceBetween={12}
            >
              {data.map((item, i) => (
                <SwiperSlide key={i}>
                  <BannerItem onClick={() => window.location.replace(item.url)}>
                    <img src="/image-web/search/banner-icon.svg" alt="icon" />
                    <div className="content">
                      <div className="title">{item.title}</div>
                      <div className="sub-title">{item.subTitle}</div>
                    </div>
                  </BannerItem>
                </SwiperSlide>
              ))}
            </Swiper>
          </MapBannerSwiperWrapper>
        </>
      )}
      {recommendedList.length === 0 ? <></> : <PremiumStoreList />}
      <FilterComponent />
      <StoreList />
    </MapBannerWrapper>
  );
};
